/**
 * PATH: packages/ui/src/kanban/components/comment-text/CommentBody.tsx
 * FILE_ID: UI-KANBAN-COMMENT-TEXT-002
 * PURPOSE: Shared comment/note/message body renderer — URLs as short-form
 *   clickable chips, optional @mention highlights inside text segments.
 * WHY: FEAT-2026-07-06-KANBAN-COMMENT-LINKS. One pipeline for Team chat,
 *   card notes, peek, and Trello mirror so pasted https://… is always
 *   clickable. stopPropagation on the anchor is load-bearing: rows live
 *   inside cards whose click opens the drawer.
 * FLOW:
 *   text → splitLinkSegments → link chips | MentionRuns (text only)
 */
import { ExternalLink } from 'lucide-react'

import type { KanbanMember } from '../../api'
import { resolveMemberToken } from '../../mentionTokens'
import { splitLinkSegments } from './linkify'

export function memberLabel(m: KanbanMember): string {
  return (m.name || '').trim() || m.email || `User #${m.id}`
}

/** Match either @here / @all (broadcast) or @<word> (member mention). */
const MENTION_RE = /(@here\b|@all\b|@[A-Za-z0-9._-]+)/g

const LINK_CHIP_CLASS =
  'mx-0.5 inline-flex max-w-full items-center gap-0.5 rounded bg-blue-50 px-1 py-px align-baseline font-medium text-blue-700 underline decoration-blue-300 underline-offset-2 hover:bg-blue-100 hover:text-blue-800 dark:bg-blue-950/40 dark:text-blue-300 dark:decoration-blue-700 dark:hover:bg-blue-950/70'

function MentionRuns({
  text,
  mentionIds,
  members,
  membersById,
  currentUserId,
  keyPrefix,
}: {
  text: string
  /** When defined → team-chat mode (highlight all @tokens; resolve via ids). */
  mentionIds: number[] | undefined
  members: KanbanMember[]
  membersById: Map<number, KanbanMember>
  currentUserId?: number | null
  keyPrefix: string
}) {
  const parts = text.split(MENTION_RE)
  const teamChatMode = mentionIds !== undefined

  return (
    <>
      {parts.map((part, i) => {
        if (!part) return null
        if (part === '@here' || part === '@all') {
          // Broadcast tokens only highlight in Team chat; card notes leave them plain.
          if (!teamChatMode) {
            return <span key={`${keyPrefix}-${i}`}>{part}</span>
          }
          return (
            <span
              key={`${keyPrefix}-${i}`}
              className="rounded bg-amber-100 px-1 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300"
            >
              {part}
            </span>
          )
        }
        if (part.startsWith('@')) {
          const handle = part.slice(1).toLowerCase()
          let resolved: KanbanMember | undefined

          if (teamChatMode) {
            for (const id of mentionIds) {
              const m = membersById.get(id)
              if (m && memberLabel(m).toLowerCase().startsWith(handle)) {
                resolved = m
                break
              }
            }
          } else {
            resolved = resolveMemberToken(handle, members) ?? undefined
            // Card-notes: unresolved @foo stays plain text.
            if (!resolved) {
              return <span key={`${keyPrefix}-${i}`}>{part}</span>
            }
          }

          const isSelf = !!(
            resolved && currentUserId && resolved.id === currentUserId
          )
          return (
            <span
              key={`${keyPrefix}-${i}`}
              className={
                isSelf
                  ? 'rounded bg-red-100 px-1 font-semibold text-red-800 dark:bg-red-900/30 dark:text-red-300'
                  : 'rounded bg-blue-100 px-1 font-semibold text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
              }
              title={resolved ? memberLabel(resolved) : part}
            >
              {part}
            </span>
          )
        }
        return <span key={`${keyPrefix}-${i}`}>{part}</span>
      })}
    </>
  )
}

export interface CommentBodyProps {
  text: string
  className?: string
  /** Org directory — enables @mention highlighting in text segments. */
  members?: KanbanMember[]
  /** Prefer when already built (Team chat). Overrides `members` for lookup. */
  membersById?: Map<number, KanbanMember>
  /**
   * Server-validated mention ids (Team chat). When provided, @tokens always
   * highlight and resolve against these ids. When omitted, only tokens that
   * resolve via `resolveMemberToken` against `members` highlight (card notes).
   */
  mentionIds?: number[]
  currentUserId?: number | null
}

/**
 * Render a body with URLs as short-form clickable chips. When members (or
 * mentionIds) are supplied, @tokens inside text segments are highlighted —
 * never inside URL paths.
 */
export function CommentBody({
  text,
  className,
  members,
  membersById: membersByIdProp,
  mentionIds,
  currentUserId,
}: CommentBodyProps) {
  if (!text) return null

  const membersList = members ?? []
  const membersById =
    membersByIdProp ?? new Map(membersList.map((m) => [m.id, m] as const))

  const wantMentions =
    mentionIds !== undefined || membersList.length > 0 || membersById.size > 0

  const segments = splitLinkSegments(text)

  return (
    <span className={className ?? 'whitespace-pre-wrap break-words'}>
      {segments.map((seg, i) =>
        seg.kind === 'link' ? (
          <a
            key={i}
            href={seg.url}
            target="_blank"
            rel="noopener noreferrer"
            title={seg.url}
            onClick={(e) => e.stopPropagation()}
            className={LINK_CHIP_CLASS}
          >
            <span className="truncate">{seg.label}</span>
            <ExternalLink className="h-2.5 w-2.5 shrink-0" aria-hidden />
          </a>
        ) : wantMentions ? (
          <MentionRuns
            key={i}
            text={seg.text}
            mentionIds={mentionIds}
            members={membersList}
            membersById={membersById}
            currentUserId={currentUserId}
            keyPrefix={String(i)}
          />
        ) : (
          <span key={i}>{seg.text}</span>
        ),
      )}
    </span>
  )
}
